import useAuth from '@app/auth/use-auth';
import { useCCF } from '@app/ccf/company-report/ccf-context';
import { reportSlugs } from '@app/ccf/company-report/slugs';
import PreviewSheet from '@app/components/preview/preview-sheet';
import { PreviewSheetBarChart } from '@app/components/preview/preview-sheet-bar-chart';
import PreviewSheetSection from '@app/components/preview/preview-sheet-chart-section';
import { useAccount } from '@shared/api';
import { useGetFoodPreview } from '@shared/api/hooks/ccf/activities/food';
import { EmissionsCategoryEnum } from '@shared/api/types';
import { itemsByRatingChartConfig } from '@shared/components/chart/configs';
import SimplePieChart from '@shared/components/chart/simple-pie-chart';
import Text from '@shared/components/content/text';
import { useGetFilterLabels } from '@shared/components/data-table/hooks/use-get-filter-labels';
import RatingCloud from '@shared/components/ratings/rating-cloud';
import { ChartConfig } from '@shared/components/ui/chart';
import { formatCompact, getCurrencySymbol } from '@shared/lib/format';
import {
  capitalizeFirstLetter,
  formatNumber,
  RatingToTextEnum,
} from '@shared/lib/utils';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export default function FoodPreviewSheet() {
  const [searchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);

  const time_periods = searchParams.get('period')?.split('.');
  const suppliers = searchParams.get('supplier')?.split('.');
  const sites = searchParams.get('site')?.split('.');

  const { flatLabels: tags } = useGetFilterLabels([
    'supplier',
    'period',
    'site',
  ]);

  const { uuid: reportUuid, categories } = useCCF();

  const category = categories?.find(
    (item) => item.slug === reportSlugs.foodAndDrink
  );

  const { data: foodPreviewData, status } = useGetFoodPreview({
    companyReportUuid: reportUuid!,
    params: {
      suppliers,
      time_periods,
      sites,
    },
    enabled: true,
  });

  const { user } = useAuth();
  const hasCostFeature = !!user?.features.cost;
  const { data: account } = useAccount(true);

  const totalEmissionsTonnes = foodPreviewData?.totalEmissionsTonnes;
  const totalWeightKg = foodPreviewData?.totalWeightKg;
  const totalSpend =
    foodPreviewData?.totalSpend != null
      ? Number(foodPreviewData.totalSpend)
      : null;

  const emissionsByFoodCategory = foodPreviewData?.foodEmissionsByCategory
    ?.slice(0, 10)
    ?.map((category) => {
      return {
        category: capitalizeFirstLetter(category.category),
        emissions: category.totalEmissionsTonnes,
        fill: 'var(--chart-3)',
      };
    });

  const itemsByRating = foodPreviewData?.itemsByRating
    ?.map((category) => {
      return {
        category: category.rating,
        items: category.numberOfItems,
        fill: `var(--rating-${category.rating.toLowerCase()})`,
      };
    })
    .sort((a, b) => a.category.localeCompare(b.category));

  const numberOfItems = foodPreviewData?.itemsByRating?.reduce(
    (acc, category) => {
      return acc + category.numberOfItems;
    },
    0
  );

  const chartConfig = {
    emissions: {
      label: 'Emissions',
      color: 'hsl(var(--chart-1))',
      unit: 'tCO2e',
    },
  } satisfies ChartConfig;

  return (
    <PreviewSheet
      title={`Scope ${category?.scope} - ${category?.name}`}
      status={status}
      metrics={[
        {
          title: 'Total emissions',
          value: formatNumber(totalEmissionsTonnes!),
          unit: 'tCO2e',
        },
        {
          title: 'Total food weight',
          value: formatNumber(totalWeightKg! / 1000),
          unit: 't',
        },
        ...(hasCostFeature && totalSpend != null
          ? [
              {
                title: 'Total spend',
                value: (
                  <>
                    {/* Currency symbol matches the gray-400 unit colour so it
                        reads as a unit rather than blending into the figure. */}
                    <span className="mr-1 text-gray-400">
                      {getCurrencySymbol(account?.defaultCurrency)}
                    </span>
                    {formatCompact(totalSpend)}
                  </>
                ),
                unit: '',
              },
            ]
          : []),
      ]}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      tags={tags}
    >
      {foodPreviewData && (
        <>
          <PreviewSheetSection title="Food items by rating">
            <div className="flex items-center space-y-2 overflow-visible">
              <div className="relative overflow-visible">
                <SimplePieChart
                  dataKey="items"
                  nameKey="category"
                  chartConfig={itemsByRatingChartConfig}
                  chartData={itemsByRating!.reverse()}
                  innerRadius={50}
                  className="h-40 w-48"
                  margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                  centerLabel={{
                    value: numberOfItems!,
                    label: 'items',
                  }}
                />
              </div>
              <div className="flex h-full flex-col gap-y-2">
                {(
                  ['A', 'B', 'C', 'D', 'E'] as Array<
                    keyof typeof RatingToTextEnum
                  >
                ).map((rating, idx) => {
                  return (
                    <div key={idx} className="flex items-center gap-2">
                      <RatingCloud
                        rating={rating as EmissionsCategoryEnum}
                        className="w-9"
                      />
                      <Text>{RatingToTextEnum[rating]}</Text>
                    </div>
                  );
                })}
              </div>
            </div>
          </PreviewSheetSection>

          <PreviewSheetSection
            title="Emissions by food family"
            unit="tCO2e"
            className="h-full"
          >
            <PreviewSheetBarChart
              nameKey="category"
              chartData={emissionsByFoodCategory}
              chartConfig={chartConfig}
            />
          </PreviewSheetSection>
        </>
      )}
    </PreviewSheet>
  );
}
